import { useAuthStore } from './useAuthStore'
import { PlatformSection, AccessLevel } from '@/types'

export function usePermissions() {
  const { clinicsList, activeClinicId } = useAuthStore()

  const activeClinic = clinicsList.find(c => c.id === activeClinicId)

  const checkLevel = (current: AccessLevel, required: AccessLevel): boolean => {
    if (current === 'write') return true
    if (current === 'read' && (required === 'read' || required === 'none')) return true
    if (current === 'none' && required === 'none') return true
    return false
  }

  const hasPermission = (section: PlatformSection, requiredLevel: AccessLevel = 'read'): boolean => {
    // No active clinic means no access
    if (!activeClinic) return false

    // Owners always have full access
    if (activeClinic.is_owner) return true

    // Check section permissions from the active clinic membership
    const permissions = activeClinic.permissions
    if (!permissions || permissions.length === 0) return false

    const perm = permissions.find(p => p.section === section)
    if (!perm) return false

    return checkLevel(perm.access_level, requiredLevel)
  }

  return {
    hasPermission,
    canRead: (section: PlatformSection) => hasPermission(section, 'read'),
    canWrite: (section: PlatformSection) => hasPermission(section, 'write'),
    isOwner: !!activeClinic?.is_owner,
  }
}

export function PermissionGuard({
  section,
  level = 'read',
  children,
  fallback = null,
}: {
  section: PlatformSection
  level?: AccessLevel
  children: React.ReactNode
  fallback?: React.ReactNode
}) {
  const { hasPermission } = usePermissions()

  if (!hasPermission(section, level)) {
    return <>{fallback}</>
  }

  return <>{children}</>
}
